import moment from 'moment';
import localStorageService from '../services/localStorageService';
import Util from '../util/util';

const createBy = localStorageService.getCurrentUser();
const dateFormat = 'YYYY/MM/DD';
export const stringToMoment = (dateString) => moment(dateString, dateFormat);

const merge = (arr1, arr2) => {
  return arr1.map((number, index) => {
    return number + arr2[index];
  });
};

const calculateFinishDate = (startDate, validity) => {
  const validNumber = Number(validity || 0);
  if (!startDate || !validNumber) {
    return '';
  }
  const wholeMonths = Math.floor(validNumber);
  const extraWeeks = (validNumber - wholeMonths) * 4;
  return moment(startDate, dateFormat)
    .add(wholeMonths, 'months')
    .add(extraWeeks, 'weeks')
    .format(dateFormat);
};

const buildCourseTypeDisplayName = (courseType, courseName) =>
  [courseType, courseName].filter((item) => item).join(' ');

const enrollStudentDataTransform = (rawData) => {
  const { datepick } = rawData;
  let createByInfo = createBy ?? localStorageService.getCurrentUser();
  const studentDOB = datepick ? datepick.format(dateFormat) : null;
  const data = { ...rawData, createBy: createByInfo };
  if (studentDOB) {
    data.studentDOB = studentDOB;
  }
  delete data.datepick;
  return data;
};

const enrollmentDateTransform = (rawData) => {
  let { student_id, total_price, datepick, ...rest } = rawData;
  let enrollData = {
    ...rest,
    student: student_id,
    total_price: parseInt(total_price),
    balance: parseInt(total_price),
    enrollDate: datepick ? datepick.format(dateFormat) : null,
    createBy: localStorageService.getCurrentUser() || createBy,
  };
  if (enrollData.course_advisor === 'NONE') {
    delete enrollData.course_advisor;
  }
  return enrollData;
};

const transactionDataTransform = (rawData) => {
  let {
    currency,
    enrollment,
    method,
    receiver,
    student_id,
    total_aud,
    total_paid,
    comments,
    datepick,
  } = rawData;
  const totalAud = parseInt(total_aud);

  const transactionData = {
    student: student_id,
    enrollment,
    receive_price: {
      total_paid: parseInt(total_paid || totalAud),
      currency: currency || 'AUD',
      method: method || 'TRANSFER',
    },
    total_aud: totalAud,
    receiver,
    comments,
    transactionDate: datepick ? datepick.format(dateFormat) : null,
    createBy,
  };
  return transactionData;
};

const feedbackDataTransform = (rawData) => {
  let { datepick, student_id, ...rest } = rawData;
  return {
    ...rest,
    student: student_id,
    feedbackDate: datepick ? datepick.format(dateFormat) : null,
    createBy: localStorageService.getCurrentUser() || createBy,
  };
};

const enrollmentListDataTransform = (income) => {
  // console.log('enrollmentListDataTransform: ', income);
  let { total, item, totalEnrollPrice, totalBalance } = income;
  const visibleItems = item.filter((enrollment) => !enrollment.archived);
  let displayData = visibleItems.map((enrollment, index) => {
    const courseName =
      enrollment.courseName || (enrollment.course && enrollment.course.name);
    const courseType = enrollment.courseType || '';
    const courseDisplayName = enrollment.courseDisplayName || courseName;
    return {
      key: enrollment._id,
      archived: enrollment.archived,
      student_school_id: enrollment.student_school_id,
      studentName: enrollment.studentName,
      reEnroll: enrollment.reEnroll ? 1 : 0,
      feedbackCounter: enrollment.feedbackCounter,
      attendanceCounter: enrollment.attendanceCounter,
      lessonCount: enrollment.lessonCount,
      remainingLessons: enrollment.remainingLessons,
      courseValidity:
        enrollment.courseValidity || (enrollment.course && enrollment.course.validity),
      courseDuration: enrollment.courseDuration || (enrollment.course && enrollment.course.duration),
      courseWeight:
        enrollment.courseWeight || (enrollment.course && enrollment.course.weight),
      finishDate: calculateFinishDate(
        enrollment.startDate,
        enrollment.courseValidity ||
          (enrollment.course && enrollment.course.validity)
      ),
      lastActiveDate: enrollment.lastActiveDate,
      courseTypeName: courseType,
      courseDisplayName,
      courseTypeDisplayName: buildCourseTypeDisplayName(
        courseType,
        courseDisplayName
      ),
      teacherName: enrollment.teacherName,
      advisorName: enrollment.advisorName,
      enrollDate: enrollment.enrollDate,
      startDate: enrollment.startDate,
      total_price: enrollment.total_price,
      balance: enrollment.balance,
      student: enrollment.student,
      comments: enrollment.comments,
      paid: enrollment.total_price - enrollment.balance,
    };
  });
  return { total, displayData, totalEnrollPrice, totalBalance };
};

const attendanceListDataTransform = (income) => {
  console.log('attendanceListDataTransform: ', income);
  let { total, item } = income;
  let displayData = item.map((attendance, index) => {
    const attendanceCourseName =
      attendance.currentEnrollmentName ||
      (attendance.currentEnrollment && attendance.currentEnrollment.courseName);
    return {
      key: attendance._id,
      student: attendance.student,
      student_school_id: attendance.student_school_id,
      attendTime: attendance.attendTime
        ? attendance.attendTime
        : attendance.attendDate,
      studentName: attendance.cnname,
      courseName: attendanceCourseName,
      courseTypeDisplayName:
        attendance.currentEnrollmentDisplayName ||
        buildCourseTypeDisplayName(
          attendance.currentEnrollmentType ||
            (attendance.currentEnrollment &&
              attendance.currentEnrollment.courseType),
          attendanceCourseName
        ) ||
        attendanceCourseName,
      teacherName: attendance.currentTeacherName,
      advisorName: attendance.currentAdvisorName,
      enrollDate: attendance.currentEnrollmentDate,
      toNow: attendance.toNow,
      balance: attendance.currentBalance ? attendance.currentBalance : 0,
    };
  });
  return { total, displayData };
};

const studentBasicDataTransform = (income) => {
  const {
    student_school_id,
    other_info,
    cnname,
    enname,
    phone,
    email,
    wechat,
    studentDOB,
    currentEnrollment,
    currentEnrollmentName,
    createBy,
    updatedAt,
  } = income;
  return {
    student_school_id,
    other_info,
    cnname,
    enname,
    phone,
    email,
    wechat,
    studentDOB: stringToMoment(studentDOB),
    currentEnrollment,
    currentEnrollmentName,
    createBy,
    updatedAt: Util.toLocalTime(updatedAt),
  };
};

const studentPatchDataTransform = (income) => {
  const {
    student_school_id,
    other_info,
    datepick,
    cnname,
    enname,
    phone,
    email,
    wechat,
  } = income;
  return {
    student_school_id,
    other_info,
    studentDOB: datepick ? datepick.format(dateFormat) : null,
    cnname,
    enname,
    phone,
    email,
    wechat,
    createBy,
  };
};

const studentEnrollCourseDataTransform = (income) => {
  let displayData = income.map((enrollment, index) => {
    const courseName =
      enrollment.courseName ||
      (enrollment.course && enrollment.course.name);
    const courseType = enrollment.courseType || '';
    return {
      key: enrollment._id,
      reEnroll: enrollment.reEnroll ? 1 : 0,
      courseTypeName: courseType,
      courseName,
      courseTypeDisplayName: buildCourseTypeDisplayName(courseType, courseName),
      feedbackCounter: enrollment.feedbackCounter,
      lessonCount: enrollment.lessonCount,
      teacherName: enrollment.teacherName,
      advisorName: enrollment.advisorName,
      enrollDate: enrollment.enrollDate,
      total_price: enrollment.total_price,
      balance: enrollment.balance,
      student: enrollment.student,
      createBy: enrollment.createBy,
      updatedAtRaw: enrollment.updatedAt,
      updatedAt: Util.toLocalTime(enrollment.updatedAt),
    };
  });
  return displayData;
};

const studentTransactionDataTransform = (income) => {
  if (income === []) return [];
  let displayData = income.map((item, index) => {
    const transactionCourseName =
      (item.enrollment && item.enrollment.courseName) || item.courseName;
    const transactionCourseType =
      (item.enrollment && item.enrollment.courseType) || item.courseType;
    return {
      key: item._id,
      courseName: transactionCourseName,
      courseTypeDisplayName:
        buildCourseTypeDisplayName(transactionCourseType, transactionCourseName) ||
        item.courseTypeDisplayName ||
        transactionCourseName,
      total_paid: item.receive_price.total_paid,
      currency: item.receive_price.currency,
      method: item.receive_price.method,
      total_aud: item.total_aud,
      receiverName: item.receiverName,
      transactionDate: item.transactionDate,
      comments: item.comments,
    };
  });
  return displayData;
};

const transactionSummaryDataTransform = (income) => {
  if (income === []) return [];
  let { total, totalTransaction, item } = income;
  // console.log('transaction list: ', income);
  let displayData = item.map((transaction, index) => {
    const transactionCourseName =
      (transaction.enrollment && transaction.enrollment.courseName) ||
      transaction.courseName;
    const transactionCourseType =
      (transaction.enrollment && transaction.enrollment.courseType) ||
      transaction.courseType;
    return {
      key: transaction._id,
      student: transaction.student,
      studentName: transaction.studentName,
      courseName: transactionCourseName,
      courseTypeDisplayName:
        buildCourseTypeDisplayName(transactionCourseType, transactionCourseName) ||
        transaction.courseTypeDisplayName ||
        transactionCourseName,
      total_paid: transaction.receive_price.total_paid,
      currency: transaction.receive_price.currency,
      method: transaction.receive_price.method,
      total_aud: transaction.total_aud,
      receiverName: transaction.receiverName,
      transactionDate: transaction.transactionDate,
      comments: transaction.comments,
      advisor:
        transaction.enrollment == null
          ? '无'
          : transformTransactionEnrollment(transaction.enrollment).advisorName,
      trialTeacher:
        transaction.enrollment == null
          ? ''
          : transformTransactionEnrollment(transaction.enrollment).trialTeacher,
    };
  });
  // console.log('display data list: ', displayData);
  return { total, totalTransaction, displayData };
};

const transformTransactionEnrollment = (enrollment) => {
  let advisorName = '无';
  let trialTeacher = '';

  if (enrollment.advisorName) {
    advisorName = enrollment.advisorName;
  }

  if (enrollment.trialTeacher && enrollment.trialTeacher.cnname) {
    trialTeacher = enrollment.trialTeacher.cnname;
  }
  return { advisorName, trialTeacher };
};

const feedbackDetailDataTransform = (income) => {
  let { total, item } = income;
  let displayData = item.map((feedback, index) => {
    return {
      key: feedback._id,
      student: feedback.student,
      studentName: feedback.studentName,
      teacherName: feedback.teacherName,
      courseName: feedback.courseName,
      courseTypeDisplayName:
        feedback.courseTypeDisplayName ||
        buildCourseTypeDisplayName(
          feedback.courseCategory ||
            (feedback.enrollment && feedback.enrollment.courseType),
          feedback.courseName
        ) ||
        feedback.courseName,
      courseType: feedback.courseType,
      content: feedback.content,
      feedbackDate: feedback.feedbackDate,
      courseEndReminder: feedback.courseEndReminder,
    };
  });
  return { total, displayData };
};

export default {
  merge,
  enrollStudentDataTransform,
  transactionDataTransform,
  enrollmentDateTransform,
  attendanceListDataTransform,
  feedbackDataTransform,
  enrollmentListDataTransform,
  studentBasicDataTransform,
  studentPatchDataTransform,
  studentEnrollCourseDataTransform,
  studentTransactionDataTransform,
  feedbackDetailDataTransform,
  transactionSummaryDataTransform,
};
